import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicOrderStatusPoller, loadCheckoutSession, clearCheckoutSession, resolveAstroOrderId } from '../checkout/useCheckout';
import { publicApi, setApiRole } from '../api/api';

const SESSION_RETRY_ATTEMPTS = 4;
const SESSION_RETRY_DELAY_MS = 800;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retrieve the post-payment session (auth tokens) created by the payment.captured webhook.
 * That session row is written near the END of the webhook — a beat after the order is
 * marked paid — so the first call can legitimately 404 (a sub-second race). Retry a few
 * times before giving up; the order-success page still works unauthenticated if no
 * session ever appears, so this is best-effort login, never fatal.
 */
async function retrievePaymentSession(orderId) {
  for (let attempt = 1; attempt <= SESSION_RETRY_ATTEMPTS; attempt++) {
    try {
      const res = await publicApi.get(`/payment/session-by-order/${orderId}`);
      if (res.data?.status === 'success') {
        const { token, user } = res.data.data;
        if (token?.accessToken) {
          localStorage.setItem('userAccessToken', token.accessToken);
          localStorage.setItem('userRefreshToken', token.refreshToken);
          localStorage.setItem('user', JSON.stringify(user));
        }
        return true;
      }
    } catch (err) {
      // 404 = session not created yet; keep retrying. Any other error: stop early.
      if (err?.response && err.response.status !== 404) break;
    }
    if (attempt < SESSION_RETRY_ATTEMPTS) await sleep(SESSION_RETRY_DELAY_MS);
  }
  return false;
}

/**
 * Handles the browser redirect back from Razorpay OR Cashfree after
 * netbanking / wallet / card payment.
 *
 * Razorpay redirects here with query params:
 *   Success: ?razorpay_payment_id=pay_xxx&razorpay_order_id=order_xxx&razorpay_signature=xxx
 *   Failure: ?error[code]=BAD_REQUEST_ERROR&error[description]=...
 *
 * Cashfree redirects here (via the backend's cashfreeCallback, see the plan)
 * with a single param: ?order_id=<astroOrderId> — already OUR OWN order id
 * (Cashfree lets us choose its order_id at creation time), so unlike
 * Razorpay's opaque order id, no resolve-by-gateway-order-id lookup is ever
 * needed for the Cashfree path.
 */
function CheckoutRedirectCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'failed' | 'delayed'
  const [errorDescription, setErrorDescription] = useState('');
  const { startPublicPolling } = usePublicOrderStatusPoller();

  useEffect(() => {
    // Landing here means CheckoutPage's UPI-intent attempt is over (either
    // gateway's redirect) — its crash-recovery flag must not outlive it, or
    // the next checkout in this tab inherits a stuck "processing" state.
    sessionStorage.removeItem('upi_intent_pending');

    const params = new URLSearchParams(window.location.search);
    const paymentId      = params.get('razorpay_payment_id');
    const razorpayOrderId = params.get('razorpay_order_id');
    const cashfreeAstroOrderId = params.get('order_id'); // Cashfree only — already our astroOrderId
    const errorCode       = params.get('error[code]');
    const errorDesc       = params.get('error[description]') || '';

    const session = loadCheckoutSession();

    // Cashfree-only failure wording (2026-10-17). Cashfree's return_url never
    // carries Razorpay's error[description], so the generic "Please contact
    // support" default below was shown even when the customer simply
    // cancelled at the bank page. Backend maps cancel/drop and decline to the
    // same 'failed' status, so the message must be true for both. Detected by
    // Cashfree's own redirect param (cashfree_order_id) or the session's
    // gateway — and never when any razorpay_* param is present — so the
    // Razorpay messages are untouched.
    const isCashfreeReturn = !paymentId && !razorpayOrderId
      && (Boolean(params.get('cashfree_order_id')) || session?.order?.gateway === 'cashfree');
    const cashfreeFailedMessage = 'Payment was not completed. You can try again.';

    // Razorpay-specific heuristic (a Cashfree redirect never carries
    // razorpay_payment_id, so this must not run for the Cashfree branch below
    // — it's only consulted once we already know we're on the Razorpay path).
    const urlLooksFailed = Boolean(errorCode || !paymentId);

    // Always verify against our own backend before showing a failure state. A missing
    // razorpay_payment_id or an error[code] in the redirect URL is NOT proof the
    // payment failed — in-app browsers, ad-blockers, and CDNs can strip or mangle
    // redirect query params even when Razorpay actually captured the payment.
    const goToOrderSuccess = async (astroOrderId) => {
      setStatus('success');
      clearCheckoutSession();

      // Always set role to 'user' before navigating to order success,
      // so the api interceptor never falls back to admin token / admin redirect.
      setApiRole('user');

      // Try to retrieve the payment session (tokens) created by the webhook.
      // If it succeeds, log the user in so order-details / PrivateUserRoute works.
      // Retries a few times because the session row lands a beat after the order is
      // marked paid; non-fatal if it never appears (order success works without auth).
      await retrievePaymentSession(astroOrderId);

      navigate(`/order-success/ORD_${String(astroOrderId).padStart(6, '0')}`);
    };

    // Poll our own order-status endpoint — the single source of truth for a fully
    // processed order (invoice, WhatsApp confirmation, lead conversion all happen
    // together in the webhook that flips this status). A payment can show as captured
    // on Razorpay's side before that webhook finishes, so a poll timeout is NOT proof
    // of failure — only an explicit 'failed' status from our own backend is. On
    // timeout we tell the customer honestly instead of guessing; the order still
    // completes on its own once the webhook lands (Razorpay retries it automatically).
    const pollForOrder = (astroOrderId, sessionForRetry) => {
      startPublicPolling(astroOrderId, {
        onSuccess: () => goToOrderSuccess(astroOrderId),
        onFailure: () => {
          setStatus('failed');
          setErrorDescription(isCashfreeReturn ? cashfreeFailedMessage : errorDesc);
          setTimeout(() => {
            if (sessionForRetry) {
              navigate('/checkout', {
                state: {
                  ...sessionForRetry,
                  paymentError: isCashfreeReturn
                    ? cashfreeFailedMessage
                    : (errorDesc || 'Payment processing failed. Please contact support.'),
                },
              });
            } else {
              // No full session to resume checkout with (session was lost) — home is
              // the only safe place to send them.
              navigate('/');
            }
          }, 2500);
        },
        onTimeout: () => setStatus('delayed'),
      });
    };

    if (session?.orderId) {
      // Normal case: session survived the redirect. Gateway-agnostic already
      // — session.orderId is our own astroOrderId regardless of which
      // gateway funded it.
      pollForOrder(session.orderId, session);
      return;
    }

    // Cashfree, session lost (e.g. in-app browser): the redirect already
    // carries our own astroOrderId directly (see this function's doc comment)
    // — no resolve-by-gateway-order-id lookup needed, unlike Razorpay below.
    if (cashfreeAstroOrderId) {
      pollForOrder(cashfreeAstroOrderId, null);
      return;
    }

    // Session missing — typically an in-app browser (Instagram/Facebook) opened the
    // post-payment redirect in a fresh browsing context that never saw /checkout's
    // sessionStorage. Recover our astro order id from Razorpay's own order id in the
    // URL instead of trusting URL params, then fall back to the exact same polling.
    if (razorpayOrderId) {
      resolveAstroOrderId(razorpayOrderId).then((astroOrderId) => {
        if (astroOrderId) {
          pollForOrder(astroOrderId, null);
        } else if (urlLooksFailed) {
          setErrorDescription(errorDesc || 'Payment was not completed. Please try again.');
          setStatus('failed');
          setTimeout(() => navigate('/'), 3000);
        } else {
          // Order not resolvable yet (e.g. webhook hasn't created the row at all) but
          // the URL doesn't look like a failure either — don't guess.
          setStatus('delayed');
        }
      });
      return;
    }

    // Nothing to poll and no order id anywhere in the URL — the only case left where
    // we fall back to trusting the redirect URL params directly.
    if (urlLooksFailed) {
      setErrorDescription(errorDesc || 'Payment was not completed. Please try again.');
      setStatus('failed');
      setTimeout(() => navigate('/'), 3000);
    } else {
      setStatus('delayed');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh',
      padding: 24,
      fontFamily: 'sans-serif',
      textAlign: 'center',
    }}>
      {status === 'verifying' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⏳</div>
          <h2 style={{ margin: 0 }}>Verifying your payment…</h2>
          <p style={{ color: '#666', marginTop: 10 }}>Please wait — do not close this page.</p>
        </>
      )}
      {status === 'success' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✅</div>
          <h2 style={{ margin: 0 }}>Payment Successful!</h2>
          <p style={{ color: '#666', marginTop: 10 }}>Redirecting to your order…</p>
        </>
      )}
      {status === 'failed' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>❌</div>
          <h2 style={{ margin: 0 }}>Payment Not Completed</h2>
          {errorDescription && (
            <p style={{ color: '#c62828', marginTop: 10, maxWidth: 400, lineHeight: 1.5 }}>
              {errorDescription}
            </p>
          )}
          <p style={{ color: '#666', marginTop: 8 }}>Redirecting…</p>
        </>
      )}
      {status === 'delayed' && (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⏳</div>
          <h2 style={{ margin: 0 }}>Confirming your payment…</h2>
          <p style={{ color: '#666', marginTop: 10, maxWidth: 400, lineHeight: 1.5 }}>
            This is taking a little longer than usual. We'll send you a confirmation on WhatsApp as soon as it's done.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: 20,
              backgroundColor: '#f57c00',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Go to Homepage
          </button>
        </>
      )}
    </div>
  );
}

export default CheckoutRedirectCallback;
